import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAdminSettings } from '../services/adminApi';

/**
 * TagEditModal - Modal for editing tags on collection clips
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close/cancel the modal
 * @param {function} props.onSave - Callback when user saves (receives tags array)
 * @param {Object|null} props.clip - Single clip object (has id, title)
 * @param {Array} props.clips - Multiple clips for bulk mode (mutually exclusive with clip)
 * @param {Array} props.initialTags - Initial tags to display (default: [])
 * @param {boolean} props.isProcessing - Whether save is in progress
 * @param {string} props.externalError - Error message from parent (e.g., from API call)
 */
export default function TagEditModal({
  isOpen,
  onClose,
  onSave,
  clip = null,
  clips = null,
  initialTags = [],
  isProcessing = false,
  maxTags = 5,
  externalError = null,
}) {
  const [tags, setTags] = useState([]);
  const [error, setError] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [adminMaxTags, setAdminMaxTags] = useState(null);
  const inputRef = useRef(null);

  // Reset tags when modal opens or initialTags change
  useEffect(() => {
    if (isOpen) {
      /* c8 ignore start */
      setTags(initialTags || []);
      /* c8 ignore stop */
      setInputValue('');
      // Focus input after modal renders
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, initialTags]);

  // Try to fetch admin settings to show the configured max tags per clip
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchAdminSettings();
        /* c8 ignore start */
        if (cancelled) return;
        /* c8 ignore stop */
        if (data && typeof data.tagging_max_per_clip === 'number') {
          setAdminMaxTags(data.tagging_max_per_clip);
        }
      } catch (err) {
        // ignore — non-admins will likely get 403; fallback to prop
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && !isProcessing) {
      onClose();
    }
  }, [isProcessing, onClose]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleEscape]);

  const handleAddTags = () => {
    if (!inputValue.trim()) return;

    // Split by comma, trim whitespace, filter empty, ensure unique
    const newTags = inputValue
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0 && !tags.includes(tag));

    /* c8 ignore start */
    if (newTags.length > 0) {
    /* c8 ignore stop */
      const available = Math.max(0, maxTags - tags.length);
      if (available <= 0) {
        setError(`Maximum ${maxTags} tags allowed`);
      } else {
        const toAdd = newTags.slice(0, available);
        setTags([...tags, ...toAdd]);
        if (newTags.length > available) {
          setError(`Maximum ${maxTags} tags allowed`);
        } else {
          setError('');
        }
      }
    }

    setInputValue('');
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (e) => {
    /* c8 ignore start */
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTags();
    }
    /* c8 ignore stop */
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSave = () => {
    onSave(tags);
  };

  if (!isOpen) {
    return null;
  }

  // Determine modal title based on mode
  const isBulkMode = clips && clips.length > 0;
  const modalTitle = isBulkMode
    ? `Edit Tags (${clips.length} clips)`
    : clip
    ? `Edit Tags: ${clip.title}`
    : 'Edit Tags';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={() => !isProcessing && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tag-edit-title"
      data-testid="tag-edit-modal"
    >
      <div
        className="bg-dark-800 rounded-lg shadow-xl max-w-2xl w-full m-4 border border-dark-600"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-dark-600">
          <h2 id="tag-edit-title" className="text-xl font-bold text-white">
            {modalTitle}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Input field */}
          <div className="mb-4">
            <label htmlFor="tag-input" className="block text-sm font-medium text-gray-300 mb-2">
              Tags (comma-separated)
            </label>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                id="tag-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="e.g., funny, highlight, clutch"
                disabled={isProcessing}
                className="flex-1 px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-gray-500 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                data-testid="tag-input"
              />
              <button
                onClick={handleAddTags}
                disabled={!inputValue.trim() || isProcessing || tags.length >= maxTags}
                className="px-4 py-2 bg-primary hover:bg-primary-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-tag-button"
              >
                Add
              </button>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-gray-400">Press Enter or click Add to add tags. Separate multiple tags with commas.</p>
                <p className="text-xs text-gray-400">{tags.length} / {maxTags}</p>
            </div>
          </div>

            {/* Show configured site limit when available */}
            <div className="mb-4">
              <p className="text-xs text-gray-400">Site limit: {adminMaxTags ?? maxTags} tags per clip</p>
            </div>

          {/* Tag badges */}
          {tags.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-300 mb-2">Current Tags:</p>
              <div className="flex flex-wrap gap-2" data-testid="tag-list">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary border border-primary/30 rounded-full text-sm"
                    data-testid={`tag-badge-${tag}`}
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      disabled={isProcessing}
                      className="hover:text-red-400 transition-colors disabled:opacity-50"
                      aria-label={`Remove tag ${tag}`}
                      data-testid={`remove-tag-${tag}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Error messages for tag limit, denied tags, or other API errors */}
          {(error || externalError) && (
            <div className="mb-4">
              <p className="text-sm text-red-400" data-testid="tag-error">
                {externalError || error}
              </p>
            </div>
          )}

          {tags.length === 0 && (
            <p className="text-sm text-gray-400 mb-4" data-testid="no-tags-message">
              No tags yet. Add some tags above.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-600 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 text-gray-300 hover:text-white border border-dark-600 rounded-md hover:bg-dark-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isProcessing}
            className="px-4 py-2 bg-primary hover:bg-primary-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-button"
          >
            {isProcessing ? 'Saving...' : 'Save Tags'}
          </button>
        </div>
      </div>
    </div>
  );
}
